import { Fragment, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { HttpError } from '@voll-intelligence/domain';
import type {
  CardId,
  FlightAlertRow,
  FlightAlertsResponse,
  SquadId,
} from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import {
  FlightAlertsDisabledError,
  flightAlertsRepository,
  type FlightAlertsFilters,
} from '@web/data/repositories';
import { t } from '@web/i18n';
import { Button, Card, PageSectionHead, Scaffold, useNow, useOrgContext } from '@web/ui/core';
import { fireAndForget, twcn } from '@web/ui/core/utils';

import { AlertDetailsRow } from '../components/alert_details';
import { AlertFilters } from '../components/alert_filters';
import { AlertRow } from '../components/alert_row';
import { AlertTiles } from '../components/alert_tiles';
import { CreateCardSheet } from '../components/create_card_sheet';

const DEFAULT_FILTERS: FlightAlertsFilters = { severity: 'all', vip: false, q: '' };

// kanban-api caches the aggregation for 15s server-side; refetching every
// 30s stays comfortably behind that without hammering it on every render.
const REFETCH_INTERVAL_MS = 30_000;

// Ticks the "atualizado há N s" line once a second.
const NOW_TICK_MS = 1_000;

// The table columns in render order; the copy comes from `alerts.columns.*`.
const COLUMN_KEYS = [
  'alerts.columns.traveler',
  'alerts.columns.flight',
  'alerts.columns.when',
  'alerts.columns.situation',
  'alerts.columns.notice',
  'alerts.columns.card',
  'alerts.columns.actions',
] as const;

const HEADER_CELL =
  'whitespace-nowrap border-b border-border bg-background px-3 py-2 text-left font-mono text-2xs font-medium uppercase tracking-eyebrow-wide text-faint-foreground';

// Placeholder rows shown while the first response is in flight.
const SKELETON_ROWS = [0, 1, 2, 3, 4];

type AlertsQuery = ReturnType<typeof useQuery<FlightAlertsResponse>>;

/** A 4xx is an answer, not a blip: a 403 (no `kanban:card:read`, URL reached
 *  directly) would answer 403 to every retry, and the disabled-feature answer
 *  is permanent until a deploy. 5xx and network failures are worth another
 *  try. One predicate drives the retry, the poll and the retry button so the
 *  three can never disagree. */
function isWorthRetrying(error: unknown): boolean {
  if (error instanceof FlightAlertsDisabledError) return false;
  return !(error instanceof HttpError) || error.status >= 500;
}

/**
 * Flight-alerts operator console: tiles (counts over the UNFILTERED set —
 * never `rows.length`, which is the filtered/capped list), a filter toolbar,
 * and the row table. A card the operator can't open (403) is still just a
 * navigation target here — the drawer is what answers for it.
 */
export function AlertsPage(): React.JSX.Element {
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FlightAlertsFilters>(DEFAULT_FILTERS);
  const [creating, setCreating] = useState<FlightAlertRow | null>(null);
  // One details row open at a time, by alertKey — it survives a poll (the key
  // is stable) and a filter change simply hides it with its row.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const query = useQuery({
    queryKey: queryKeys.alerts.flights(orgId, filters),
    queryFn: () => flightAlertsRepository.list(orgId, filters),
    // A filter change is a new query key: without this the table would blank
    // out (and fall back to the pending placeholder) on every toggle. The
    // previous rows stay on screen — dimmed — until the new ones land.
    placeholderData: keepPreviousData,
    // Mirrors the app-wide default (`core/query.ts`: `retry: 1`) so a
    // one-off blip still gets a single immediate retry before surfacing as
    // an error — except answers that will not change (disabled feature,
    // any 4xx), which would otherwise burn a retry before settling.
    // `retryDelay: 0` drops react-query's default exponential backoff (1s on
    // the first retry): with it, a real 503 in a test blows past
    // `findByText`'s default timeout before the query ever settles.
    retry: (failureCount, error) => isWorthRetrying(error) && failureCount < 1,
    retryDelay: 0,
    // Don't keep hitting kanban-api every 30s once we hold an answer that
    // will not change (panel turned off, no permission).
    refetchInterval: (q) =>
      q.state.error !== null && !isWorthRetrying(q.state.error) ? false : REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    // `AlertsBody` below already renders a dedicated error state (disabled
    // feature, generic unavailable + retry) — `GlobalErrorHandler` must not
    // ALSO toast a global error on top of it.
    meta: { silent: true },
  });

  function openCard(row: FlightAlertRow): void {
    if (!row.card) return;
    if (row.card.squadId) {
      fireAndForget(
        navigate({
          to: '/orgs/$orgSlug/squad/$squadId/card/$cardId/$tab',
          params: { orgSlug, squadId: row.card.squadId, cardId: row.card.id, tab: 'conversation' },
        }),
      );
    } else {
      fireAndForget(
        navigate({
          to: '/orgs/$orgSlug/triage/card/$cardId',
          params: { orgSlug, cardId: row.card.id },
        }),
      );
    }
  }

  // `Scaffold` is what scrolls: the org workspace pane is `h-screen
  // overflow-hidden`, so without it row 20 onwards is simply unreachable.
  return (
    <Scaffold
      body={
        <div className="mx-auto flex w-full max-w-reading-w flex-col gap-6">
          <PageSectionHead title={t('alerts.title')} sub={t('alerts.sub')} />

          {query.data ? <UpdatedAgo generatedAt={query.data.generatedAt} /> : null}

          {query.data ? <AlertTiles counts={query.data.counts} /> : null}

          <AlertFilters filters={filters} onChange={setFilters} />

          <Card>
            <AlertsBody
              query={query}
              expandedKey={expandedKey}
              onToggle={(key) => {
                setExpandedKey((current) => (current === key ? null : key));
              }}
              onOpenCard={openCard}
              onCreateCard={(row) => {
                setCreating(row);
              }}
            />
          </Card>

          {/* `truncated` can arrive with few rows too (the upstream scan was cut
          short) — the copy never claims "showing N of X". */}
          {query.data?.truncated ? (
            <p className="m-0 text-meta text-muted-foreground">{t('alerts.truncated')}</p>
          ) : null}

          {creating ? (
            <CreateCardSheet
              row={creating}
              onClose={() => {
                setCreating(null);
              }}
              onCreated={(cardId, squadId) => {
                setCreating(null);
                openCard({
                  ...creating,
                  card: {
                    id: cardId as CardId,
                    seq: 0,
                    squadId: squadId as SquadId | null,
                    assigneeName: null,
                  },
                });
              }}
            />
          ) : null}
        </div>
      }
    />
  );
}

/**
 * What goes inside the card: the pending placeholder, one of the two
 * no-data states, the empty state, or the table itself.
 *
 * react-query flips `status` (and so `isError`) to 'error' on ANY failed
 * fetch, including a background poll that fails after rows were already
 * loaded — `query.data` still holds the last-good response then. Only treat
 * this as a full error state (replacing the table) when there is no data to
 * fall back on; otherwise the stale rows stay on screen.
 */
function AlertsBody({
  query,
  expandedKey,
  onToggle,
  onOpenCard,
  onCreateCard,
}: Readonly<{
  query: AlertsQuery;
  expandedKey: string | null;
  onToggle: (alertKey: string) => void;
  onOpenCard: (row: FlightAlertRow) => void;
  onCreateCard: (row: FlightAlertRow) => void;
}>): React.JSX.Element {
  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2 p-5" role="status" aria-label={t('alerts.loading')}>
        {SKELETON_ROWS.map((i) => (
          <span key={i} className="block h-8 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }
  // No data at all: a genuine no-data state, so the table gives way to it.
  // (A poll that fails AFTER rows loaded keeps `query.data` — see above.)
  if (!query.data) {
    if (query.error instanceof FlightAlertsDisabledError) {
      return <p className="p-5 text-meta text-muted-foreground">{t('alerts.disabled')}</p>;
    }
    return (
      <div className="flex flex-col items-start gap-3 p-5">
        <p className="m-0 text-meta text-tone-red-fg">{t('alerts.unavailable')}</p>
        {isWorthRetrying(query.error) ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fireAndForget(query.refetch());
            }}
          >
            {t('alerts.retry')}
          </Button>
        ) : null}
      </div>
    );
  }
  if (query.data.rows.length === 0) {
    return <p className="p-5 text-meta text-muted-foreground">{t('alerts.empty')}</p>;
  }
  return (
    <div className={twcn('overflow-x-auto', query.isPlaceholderData && 'opacity-60')}>
      <table className="w-full border-collapse text-meta">
        <thead>
          <tr>
            {COLUMN_KEYS.map((key) => (
              <th key={key} className={HEADER_CELL}>
                {t(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {query.data.rows.map((row) => (
            // A fragment per row: the details `<tr>` must be a sibling of its
            // row (not nested) for the table grid to hold.
            <Fragment key={row.alertKey}>
              <AlertRow
                row={row}
                expanded={row.alertKey === expandedKey}
                onToggle={() => {
                  onToggle(row.alertKey);
                }}
                onOpenCard={() => {
                  onOpenCard(row);
                }}
                onCreateCard={() => {
                  onCreateCard(row);
                }}
              />
              {row.alertKey === expandedKey ? (
                <AlertDetailsRow row={row} colSpan={COLUMN_KEYS.length} />
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * "Atualizado há N s" — its own component so the once-a-second `useNow` tick
 * only re-renders this line, not the whole page (filters, tiles, up to 300
 * rows) in every state, including while there's nothing to display it for.
 */
function UpdatedAgo({ generatedAt }: Readonly<{ generatedAt: Date }>): React.JSX.Element {
  const now = useNow(NOW_TICK_MS);
  return (
    <p className="m-0 text-card-id text-faint-foreground">
      {t('alerts.updated', {
        seconds: Math.max(0, Math.round((now - generatedAt.getTime()) / 1000)),
      })}
    </p>
  );
}
